import {Flex, Text, useMantineTheme, type Sx} from '@mantine/core';
import React, {ReactNode} from 'react';

import {multiLineEllipsisText} from 'styles/Utilities';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {SectionRow} from 'ui-library/reactflow/nodes/BenchlingNode/v2/BenchlingNodeSection';
import {
  STATUS_COLORS,
  SectionRowStatus,
} from 'ui-library/reactflow/nodes/BenchlingNode/v2/BenchlingNodeV2Constants';

const STATUS_ICON_NAMES: Record<SectionRowStatus, BenchlingIconName> = {
  error: 'circle-xmark',
  warning: 'exclamation-triangle',
};

export interface StandardSectionRowProps {
  /** A ReactFlow handle to display on the left-hand side of this row. */
  leftHandle?: ReactNode;
  /** A ReactFlow handle to display on the right-hand side of this row. */
  rightHandle?: ReactNode;
  /** This text is displayed in small, black text on the left-hand side of the row. */
  primaryText: string;
  /** This text is displayed in small, gray text on the right-hand side of the row. */
  secondaryText?: string;
  /** An icon to be displayed to the left of `primaryText`. */
  icon?: BenchlingIconName;
  /** Status to display instead of secondaryText. Only 'error' and 'warning' are supported. */
  status?: SectionRowStatus;
  /** Description of the status to display in a tooltip when hovering over the status icon. */
  statusDescription?: string;
  /** An icon to be displayed on the right-hand side of the row. */
  secondaryIcon?: BenchlingIconName;
}

/**
 * A standard section row for a {@link BenchlingNodeV2}.
 * @package
 */
export function StandardSectionRow(props: StandardSectionRowProps): JSX.Element {
  const theme = useMantineTheme();
  const {
    leftHandle,
    rightHandle,
    primaryText,
    secondaryText,
    icon,
    status,
    statusDescription,
    secondaryIcon,
  } = props;

  const renderStatusIcon = (): ReactNode | null => {
    if (!status) {
      return null;
    }

    const iconName = STATUS_ICON_NAMES[status];
    const icon = (
      <BenchlingIcon
        icon={iconName}
        style={{
          color: theme.colors[STATUS_COLORS[status].color][STATUS_COLORS[status].colorIndex],
          height: 16,
          width: 16,
        }}
      />
    );

    if (statusDescription) {
      return <Tooltip text={statusDescription}>{icon}</Tooltip>;
    }

    return icon;
  };

  /**
   * On the right side of the row, we display one of the following (in priority order):
   * The status icon
   * The secondary icon
   * The secondary text
   */
  const renderRightSideContent = (): ReactNode => {
    if (status) {
      return renderStatusIcon();
    }

    if (secondaryIcon) {
      return (
        <BenchlingIcon icon={secondaryIcon} style={{color: theme.colors.gray[4], height: 11, width: 11}} />
      );
    }

    if (secondaryText) {
      return (
        <Text c="gray.6" fz="xs">
          {secondaryText}
        </Text>
      );
    }

    return null;
  };

  return (
    <SectionRow>
      <Flex align="center" justify="space-between" w="100%">
        {leftHandle}

        <Flex align="center" justify="space-between" sx={{flexGrow: 1, minWidth: 0}}>
          <Flex align="center" gap="xs" sx={{flex: 1, minWidth: 0}}>
            {icon && (
              <BenchlingIcon
                icon={icon}
                style={{color: theme.colors.gray[4], height: 11, width: 11, flexShrink: 0}}
              />
            )}
            <Text
              size="xs"
              sx={{
                flex: 1,
                minWidth: 0,
                maxWidth: '100%',
                ...(multiLineEllipsisText(2) as Sx),
              }}
            >
              {primaryText}
            </Text>
          </Flex>
          {renderRightSideContent()}
        </Flex>
        {rightHandle}
      </Flex>
    </SectionRow>
  );
}
